import { useTranslation } from 'react-i18next';

import { useIsCoinsCurrency } from '@/feature/betslip-sbk/hooks/use-coins-currency';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { StakeInputError, StakeInputErrors } from '@/feature/betslip-sbk/types';
import { MIN_BET_STAKE, stakeInputValidators } from '@/feature/betslip-sbk/utils/validators';
import { MIN_COINS_STAKE, coinsStakeInputValidators } from '@/feature/betslip-sbk/utils/validators-coins';
import { activeLimitsSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useShallow } from 'zustand/react/shallow';

import { isCoinsError, isSbkError } from '../../bet-inputs/hooks/use-stake-input-error-message';
import { WarningBanner } from './WarningBanner';

const SUMMARY_STAKE_ERRORS: StakeInputError[] = [
    StakeInputErrors.BelowMin,
    StakeInputErrors.ExceedMaxSingleWager,
    StakeInputErrors.ExceedStakeLimit,
    StakeInputErrors.DecimalsNotAllowed,
];

// Returns a list of unique stake input warnings
// - Stake is below the minimum stake
// - Stake exceeds the maximum single wager limit
// - Stake exceeds the market limit
// - Decimals not allowed

export const StakeInputSummaryWarnings = () => {
    const { t } = useTranslation('betslip_sbk');
    const isCoinsCurrency = useIsCoinsCurrency();
    const { formatCurrency } = useFormatCurrency();
    const { data: limits = [] } = useUserLimits({ select: activeLimitsSelector('SINGLE_WAGER') });

    const uniqueSummaryErrors = useSbkBetSlipStore(
        useShallow(state => {
            const errors = new Set<StakeInputError>();
            Object.values(state.bets).forEach(bet => {
                if (bet.stakeInputError && bet.stake && SUMMARY_STAKE_ERRORS.includes(bet.stakeInputError)) {
                    errors.add(bet.stakeInputError);
                }
            });
            return Array.from(errors);
        })
    );

    if (uniqueSummaryErrors.length === 0) {
        return null;
    }

    const warnings: string[] = [];

    for (const error of uniqueSummaryErrors) {
        if (isCoinsCurrency && isCoinsError(error)) {
            const key = coinsStakeInputValidators[error].translationKey;
            switch (error) {
                case StakeInputErrors.BelowMin:
                    warnings.push(t(key, { value: formatCurrency(MIN_COINS_STAKE) }));
                    break;
                case StakeInputErrors.DecimalsNotAllowed:
                    warnings.push(t(key));
                    break;
            }
        } else if (!isCoinsCurrency && isSbkError(error)) {
            const key = stakeInputValidators[error].translationKey;
            switch (error) {
                case StakeInputErrors.BelowMin:
                    warnings.push(t(key, { value: formatCurrency(MIN_BET_STAKE) }));
                    break;
                case StakeInputErrors.ExceedMaxSingleWager: {
                    const maxLimit = limits.find(limit => limit.type === 'SINGLE_WAGER');
                    if (maxLimit) {
                        warnings.push(t(key, { value: formatCurrency(maxLimit.amount) }));
                    }
                    break;
                }
                case StakeInputErrors.ExceedStakeLimit:
                    warnings.push(t('entry_exceed_stake_limit_summary_error'));
                    break;
            }
        }
    }

    return (
        <>
            {warnings.map((message, index) => (
                <WarningBanner key={index} message={message} />
            ))}
        </>
    );
};
